import React, {useState, useRef, useEffect} from "react"
import {get, find} from "lodash"
import includes from "lodash/includes"
import className from "classnames"
import MobileDetect from "mobile-detect"
import {loadPayments} from "@payjp/payments-js"

import {axios, tt} from "@lib/helpers"

import {NoNameFunctionReturnVoid} from "types/common"
import {formDataType} from "types/pointPurchase"

import DefaultCardLink from "components/creditCard/DefaultCardLink"
import {FormInputs} from "components/creditCard/FormInputs"

type PointPackage = {
  id: string
  point: number
  price: number
}

type defaultPaymentMethodCardType = {
  brand: string
  last4: string
  img: string
}

type User = {
  email: string
  phone_number: string
}

type AppPointPurchasesPayjpNewProps = {
  data: {
    api_key: string
    paths: {
      point_purchases_path: string
      terms_user_path: string
      privacy_policy_path: string
      new_inquiry_path: string
      payments_path: string
      return_url: string
      create_payment_flow_path: string
    }
    t: any
    user: User
    default_payment_methodc_card: defaultPaymentMethodCardType | null
    point_packages: PointPackage[]
    service_information: any
  }
}

const AppPointPurchasesPayjpNew = ({data}: AppPointPurchasesPayjpNewProps) => {
  const {
    api_key,
    paths,
    default_payment_method_card,
    point_packages,
    t,
    user,
    service_information,
  } = data

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasErrors, setHasErrors] = useState<boolean>(false)
  const [supportBrowserPayment, setSupportBrowserPayment] =
    useState<boolean>(false)
  const [isMobileChrome, setIsMobileChrome] = useState<boolean>(false)
  const [pointPackage, setPointPackage] = useState<PointPackage | undefined>(
    point_packages?.[0]
  )
  const [payments, setPayments] = useState<any>(null)
  const [widgets, setWidgets] = useState<any>(null)
  const [clientSecret, setClientSecret] = useState<string>("")
  const [paymentFlowId, setPaymentFlowId] = useState<string>("")
  const [message, setMessage] = useState<string>("")
  const mobileDetect = new MobileDetect(window.navigator.userAgent)
  const paypalRef = React.useRef(null)
  const paymentFormRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // @ts-ignore
    if (typeof paypal != "undefined" && paypal) {
      if (window.paypalButton) window.paypalButton.close()
      // @ts-ignore
      window.paypalButton = paypal.Buttons({
        style: {
          height: 55,
        },
        createOrder: () => {
          return axios
            .post("/paypal_payments", {
              point_package_id: pointPackage?.id,
            })
            .then((res: any) => {
              return res?.data?.data?.order_id
            })
            .catch(() => {
              window.location.reload()
            })
        },
        onApprove: (data: any) => {
          setIsLoading(true)
          axios
            .post("/paypal_payments/capture", {
              order_id: data?.orderID,
            })
            .finally(() => {
              window.location.reload()
            })
        },
      })
      window.paypalButton.render(paypalRef.current)
    }
  }, [pointPackage])

  const createOrUpdatePaymentFlow = async (
    pointPackage: PointPackage | undefined
  ) => {
    try {
      setIsLoading(true)
      const res = await axios.post(paths.create_payment_flow_path, {
        point_package_id: pointPackage?.id,
        payment_flow_id: paymentFlowId || null, // 2回目以降はIDを送信
      })

      setClientSecret(res.data.client_secret)
      setPaymentFlowId(res.data.payment_flow_id)

      return res.data.clientSecret
    } catch (error) {
      console.error(error)
      setMessage("決済準備に失敗しました")
      return null
    } finally {
      setIsLoading(false)
    }
  }

  // 金額変更時に実行
  useEffect(() => {
    if (default_payment_method_card) {
      return
    }

    createOrUpdatePaymentFlow(pointPackage)
  }, [pointPackage])

  // Payments.js 初期化
  useEffect(() => {
    loadPayments(api_key, {locale: "ja"}).then(setPayments)
  }, [])

  // フォームマウント
  useEffect(() => {
    if (!payments || !clientSecret) return

    let mountedForm: any = null

    const mountForm = async () => {
      const widgetInstance = payments.widgets({clientSecret})
      setWidgets(widgetInstance)

      mountedForm = widgetInstance.createForm("payment", {
        layout: "tab",
        paymentMethodOrder: ["card"],
        fields: {
          billingDetails: {
            name: "auto",
            email: "never",
            phone: "never",
          },
        },
      })

      mountedForm.mount(paymentFormRef.current)
    }

    mountForm()

    return () => mountedForm?.unmount()
  }, [payments, clientSecret])

  // 確定ボタン
  const handleConfirm = async () => {
    if (!widgets) return

    setIsLoading(true)
    setMessage("")

    try {
      const result = await widgets.confirmPayment({
        return_url: paths.return_url,
        payment_method_data: {
          billing_details: {
            email: user.email,
            phone: user.phone_number,
          },
        },
      })

      if (result.error) {
        setMessage(`エラー: ${result.error.message}`)
      } else {
        setMessage("支払い処理が開始されました")
      }
    } catch (e: any) {
      setMessage("処理中にエラーが発生しました")
      console.log(e)
    } finally {
      setIsLoading(false)
    }
  }

  const purchasePoint = (path: string, formData: formDataType) => {
    setIsLoading(true)
    axios
      .post(path, formData)
      .then((res: any) => {
        const id = res?.data?.point_purchase_id

        const poller = () => {
          axios
            .get(`/point_purchases/${id}`)
            .then((res2: any) => {
              if (includes(["success", "error"], res2?.data?.data?.status)) {
                window.location.href = paths.point_purchases_path
              } else if (res2?.data?.data?.status == "requires_action") {
                payments.handleNextAction(res2?.data?.data?.payjp_client_secret)
              } else {
                window.setTimeout(() => {
                  poller()
                }, 1000)
              }
            })
            .catch(() => {
              window.location.reload()
            })
        }

        poller()
      })
      .catch(() => {
        window.location.reload()
      })
  }
  const defaultCardFormData: formDataType = {
    point_purchase: {
      point_package_id: pointPackage?.id,
    },
  }

  return (
    <form
      className={className("AppForm", {
        "AppForm--overlay": isLoading,
      })}
      onSubmit={(e) => {
        e.preventDefault()

        if (default_payment_method_card) {
          purchasePoint(paths.point_purchases_path, defaultCardFormData)
        } else {
          handleConfirm()
        }
      }}
    >
      <h3 className="AppForm__h3">{tt(t, "point_purchases.form.title")}</h3>

      <div className="AppForm__row">
        <div className="AppForm__label">
          <span className="AppForm__label__span">
            {tt(t, "point_purchases.form.label")}
          </span>

          <span className="AppForm__label__required">
            {tt(t, "dictionary.form.required")}
          </span>
        </div>

        <div className="AppForm__value">
          <div className="AppBookingsReserve__buttons">
            <select
              className="AppSelect"
              value={pointPackage?.id}
              data-point-value={pointPackage?.point}
              name="point"
              onChange={(e) => {
                const selectedPointPackage = find(
                  point_packages,
                  (x) => x.id == e.target.value
                )
                setPointPackage(selectedPointPackage)
              }}
            >
              {(point_packages || []).map((x: PointPackage, i: number) => (
                <option key={i} value={x.id}>
                  {x.point}pt: {x.price}
                  {tt(t, "point_purchases.form.yen")}
                </option>
              ))}
            </select>

            <div className="AppBookingsReserve__buttons__row">
              <div ref={paypalRef} className="AppBookingsReserve__button"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="AppForm__divider">
        <span className="AppForm__divider__span">{t.payment_form.or}</span>
      </div>

      <h3 className="AppForm__h3">{tt(t, "point_purchases.form.sub_title")}</h3>

      {default_payment_method_card ? (
        <DefaultCardLink
          path={paths.payments_path}
          img={get(default_payment_method_card, "img")}
          last4={get(default_payment_method_card, "last4")}
          brand={get(default_payment_method_card, "brand")}
        />
      ) : (
        <React.Fragment>
          <div
            ref={paymentFormRef}
            style={{
              width: "100%",
              border: "1px solid #ddd",
              borderRadius: "8px",
              minHeight: "360px",
              overflow: "hidden",
              padding: "24px",
            }}
          />
          {message && <p style={{marginTop: "16px"}}>{message}</p>}
        </React.Fragment>
      )}

      <div className="AppForm__row">
        <div className="AppForm__value">
          <div className="AppForm__value__examples">
            <ul className="AppForm__value__examples__ul">
              <li className="AppForm__value__examples__ul__li">
                <a
                  className="AppForm__value__examples__ul__li__a"
                  href={paths.terms_user_path}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                >
                  {tt(t, "agreement.terms_of_use")}
                </a>{" "}
                {tt(t, "agreement.content")}{" "}
                <a
                  className="AppForm__value__examples__ul__li__a"
                  href={paths.privacy_policy_path}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                >
                  {tt(t, "agreement.privacy_policy")}
                </a>{" "}
                {tt(t, "agreement.content2")}
              </li>

              <li className="AppForm__value__examples__ul__li">
                {tt(t, "agreement.content3")} {service_information.service_name}{" "}
                {tt(t, "agreement.content4")}
              </li>

              <li className="AppForm__value__examples__ul__li">
                {tt(t, "agreement.content5")}
              </li>

              <li className="AppForm__value__examples__ul__li">
                {tt(t, "agreement.content6")}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="AppForm__row">
        <button
          className={className("Button Button--large", {
            "Button--disabled": isLoading || hasErrors,
          })}
          disabled={isLoading || hasErrors}
          cypress-target="submit"
        >
          {isLoading
            ? tt(t, "dictionary.form.loading")
            : tt(t, "point_purchases.form.submit")}
        </button>
      </div>

      <div className="AppForm__row">
        <p className="AppPointPurchasesNew__p">
          ポイント購入に関するお問い合わせは{" "}
          <a href={paths.new_inquiry_path}>こちら</a>
        </p>
      </div>
    </form>
  )
}

export default AppPointPurchasesPayjpNew
